import React                   from 'react';
import {
  Form,
  Field,
  Formik,
  FormikProps,
}                              from 'formik';
import styled                  from 'styled-components';
import { Grid }                from '@material-ui/core';
import {
  Button,
  TextInput,
}                              from '@components/index';
import { ILoginInitialValues } from '@typings/auth';

interface IProps {
  initialValues        : ILoginInitialValues;
  isLogging            : boolean;
  validationSchema     : object;
  isRecoveryEmailSent  : boolean;
  handleLogin          : (values: ILoginInitialValues) => Promise<void>;
  handleForgotPassword : () => void;
}

const Wrapper = styled.div`
  width: 350px;
`;

const TitleWrapper = styled.div`
  font-size   : 28px;
  font-weight : 500;
  color       : ${({ theme: { textColor } }): string => textColor.secondary};
`;

const InputWrapper = styled.div`
  position: relative;
`;

const LabelWrapper = styled.p`
  position      : absolute;
  left          : -210px;
  top           : 20px;
  width         : 200px;
  padding-right : 10px;
  font-size     : 18px;
  font-weight   : 500;
  text-align    : right;
  color         : ${({ theme: { textColor } }): string => textColor.black};
`;

const HintWrapper = styled.div`
  text-align  : center;
  white-space : nowrap;
  margin      : 5px 0 8px;
  color       : ${({ theme: { textColor } }): string => textColor.darkGray};

  p {
    font-size   : 16px;
    font-weight : 500;
  }

  span {
    font-size: 14px !important;
  }
`;

const ForgotLink = styled.div`
  display: flex;
  justify-content: flex-end;

  h2 {
    font-size  : 16px;
    text-align : right;
    display    : inline-block;

    &:hover {
      cursor          : pointer;
      text-decoration : underline;
    }
  }

`;

const StyledInput = styled(TextInput)`
  && input {
    font-weight      : 400;
    font-size        : 16px;
    height           : 35px;
    background-color : ${({ theme: { backgroundColor } }): string => backgroundColor.white};
    color            : ${({ theme: { textColor } }): string => textColor.gray} !important;
  }
`;

export const Login: React.FC<IProps> = ({
  initialValues,
  isLogging,
  validationSchema,
  isRecoveryEmailSent,
  handleLogin,
  handleForgotPassword,
}: IProps): JSX.Element => (
  <Formik
    onSubmit         = {handleLogin}
    initialValues    = {initialValues}
    validationSchema = {validationSchema}
    validateOnMount
  >
    {({
      dirty,
      errors,
      touched,
      isValid,
    }: FormikProps<ILoginInitialValues>): JSX.Element => (
      <Form>
        <Wrapper>
          <Grid container spacing={1} justify="center">
            <Grid item xs={12}>
              <TitleWrapper>Login</TitleWrapper>
            </Grid>

            <Grid item xs={12}>
              <InputWrapper>
                <LabelWrapper>Email</LabelWrapper>
                <Field
                  id          = "username"
                  name        = "username"
                  type        = "email"
                  placeholder = "Email"
                  error       = {touched.username && !!errors.username}
                  component   = {StyledInput}
                />
              </InputWrapper>
            </Grid>

            <Grid item xs={12}>
              <InputWrapper>
                <LabelWrapper>Password</LabelWrapper>
                <Field
                  id          = "password"
                  name        = "password"
                  type        = "password"
                  placeholder = "Password"
                  error       = {touched.password && !!errors.password}
                  component   = {StyledInput}
                />
              </InputWrapper>
            </Grid>

            <Grid item xs={12}>
              {isRecoveryEmailSent && (
                <HintWrapper>
                  <p>A password recovery email has been sent.</p>
                  <span>If it does not appear check your spam folder.</span>
                </HintWrapper>
              )}

              <ForgotLink onClick={handleForgotPassword}>
                <h2>Forgot Password?</h2>
              </ForgotLink>
            </Grid>

            <Grid item xs={12}>
              <Button
                type      = "submit"
                label     = "Sign In"
                margin    = "0"
                isLoading = {isLogging}
                disabled  = {!isValid || !dirty || isLogging}
                fullWidth
              />
            </Grid>
          </Grid>
        </Wrapper>
      </Form>
    )}
  </Formik>
);
